import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Users, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";

/**
 * Manager onboarding — Step 3 of 3.
 *
 * Captures `employee_limit` + `access_password` so a brand-new manager
 * can immediately receive employees WITHOUT having to go through the
 * Settings page first. Manager can also click "Later" — the gate is
 * marked answered but no values are saved, and employees can't join
 * until the manager returns to Settings.
 */
export default function ManagerEmployeeAccess() {
    const navigate = useNavigate();
    const { company, refresh } = useAuth();
    const { lang } = useLang();
    const [empLimit, setEmpLimit] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [skipping, setSkipping] = useState(false);

    if (company?.account_type !== "manager") {
        navigate("/", { replace: true });
        return null;
    }

    const submit = async () => {
        setError("");
        const n = parseInt(empLimit, 10);
        if (!Number.isFinite(n) || n < 1 || n > 999) {
            setError(lang === "ar" ? "أدخل عدداً بين 1 و 999" : "Enter a number between 1 and 999");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/set-company-access", { employee_limit: n });
            await refresh();
            toast.success(lang === "ar" ? "تم تفعيل نظام الموظفين" : "Employees system enabled");
            navigate("/employees", { replace: true });
        } catch (err) {
            const msg = formatApiError(err);
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const skipForLater = async () => {
        setSkipping(true);
        try {
            await api.post("/auth/manager/skip-access-setup");
            await refresh();
            toast.info(lang === "ar" ? "يمكنك تفعيل نظام الموظفين لاحقاً من الإعدادات" : "You can enable employees later from Settings");
            navigate("/employees", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSkipping(false);
        }
    };

    return (
        <div className="min-h-screen app-bg" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-12">
                <div className="mb-8 text-center">
                    <div className="inline-block rounded-full bg-amber-100 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-900" data-testid="mea-step-badge">
                        {lang === "ar" ? "خطوة 3 من 3 — تفعيل نظام الموظفين" : "Step 3 of 3 — Enable employees"}
                    </div>
                    <h1 className="mt-4 font-heading text-3xl font-bold text-neutral-900 sm:text-4xl">
                        {lang === "ar" ? "إعداد وصول الموظفين" : "Employees access setup"}
                    </h1>
                    <p className="mx-auto mt-3 max-w-xl text-sm text-neutral-600">
                        {lang === "ar"
                            ? "حدد عدد الموظفين المسموح لهم بالتسجيل تحت شركتك، ويكفي أن يُدخل الموظف رمز الشركة عند إنشاء حسابه."
                            : "Set the number of employees allowed to register under your company; employees only need your company code to join."}
                    </p>
                </div>

                <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-7">
                    <div className="space-y-5">
                        <div>
                            <Label className="mb-1.5 flex items-center gap-2 text-sm font-medium">
                                <Users className="h-4 w-4 text-amber-700" />
                                {lang === "ar" ? "الحد الأقصى للموظفين" : "Employee limit"}
                            </Label>
                            <Input
                                type="number"
                                min={1}
                                max={999}
                                value={empLimit}
                                onChange={(e) => setEmpLimit(e.target.value)}
                                placeholder={lang === "ar" ? "مثال: 5" : "e.g. 5"}
                                className="h-11 font-numbers"
                                data-testid="mea-limit-input"
                            />
                            <p className="mt-1.5 text-xs text-neutral-500">
                                {lang === "ar"
                                    ? "من 1 إلى 999. عندما يصل الموظفون إلى الحد، لن يتم قبول تسجيلات جديدة."
                                    : "Between 1 and 999. New signups are blocked once the limit is reached."}
                            </p>
                        </div>


                        <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900">
                            <div className="flex items-start gap-2">
                                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                                <div>
                                    {lang === "ar" ? (
                                        <>
                                            شارك رمز الشركة{" "}
                                            <span className="font-numbers font-bold">{company?.company_code || "—"}</span>{" "}
                                            مع موظفيك فقط. يمكنك تغيير عدد الموظفين لاحقاً من الإعدادات.
                                        </>
                                    ) : (
                                        <>
                                            Share your company code{" "}
                                            <span className="font-numbers font-bold">{company?.company_code || "—"}</span>{" "}
                                            only with your employees. The limit can be changed later from Settings.
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {error ? (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="mea-error">
                                {error}
                            </div>
                        ) : null}

                        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={saving || skipping}
                                        data-testid="mea-later-btn"
                                    >
                                        {lang === "ar" ? "لاحقاً" : "Later"}
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent dir={lang === "ar" ? "rtl" : "ltr"}>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>
                                            {lang === "ar" ? "تخطي إعداد الموظفين؟" : "Skip employees setup?"}
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                            {lang === "ar"
                                                ? "لن يتمكن أي موظف من التسجيل تحت شركتك حتى تعود إلى الإعدادات وتحدد عدد الموظفين."
                                                : "No employee will be able to register under your company until you return to Settings and set the employee limit."}
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel data-testid="mea-later-cancel">
                                            {lang === "ar" ? "إلغاء" : "Cancel"}
                                        </AlertDialogCancel>
                                        <AlertDialogAction onClick={skipForLater} data-testid="mea-later-ok">
                                            {lang === "ar" ? "تخطي" : "Skip"}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>

                            <Button
                                type="button"
                                size="lg"
                                onClick={submit}
                                disabled={saving || skipping}
                                data-testid="mea-save-btn"
                                className="h-11 gap-2 bg-amber-700 px-8 text-white hover:bg-amber-800"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                {lang === "ar" ? "حفظ ومتابعة" : "Save & continue"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
